import { useMemo, useState } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { fmtEUR } from "@/lib/api";
import { CaretRight, CaretDown } from "@phosphor-icons/react";

// Palette: distinct, accessible colours that work on the Factura light theme
const PALETTE = [
    "#0F766E", // teal-700
    "#F59E0B", // amber-500
    "#7C3AED", // violet-600
    "#DC2626", // red-600
    "#0EA5E9", // sky-500
    "#16A34A", // green-600
    "#EC4899", // pink-500
    "#475569", // slate-600
    "#CA8A04", // yellow-600
    "#8B5CF6", // violet-500
];

function CustomTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div className="bg-popover border border-border rounded-md shadow-md p-3 text-sm">
            <div className="font-semibold mb-1">{d.name}</div>
            <div className="text-xs text-muted-foreground">{d.count} factures · {d.percent.toFixed(1)}%</div>
            <div className="text-xs font-semibold tabular mt-1">{fmtEUR(d.total)}</div>
        </div>
    );
}

export default function SupplierPieChart({ invoices }) {
    const [expanded, setExpanded] = useState(null); // supplier name being expanded
    const data = useMemo(() => {
        if (!invoices?.length) return [];
        const grouped = new Map();
        for (const inv of invoices) {
            // Only real expenses contribute to the supplier breakdown:
            //   • facture (positive)
            //   • avoir (negative)
            // Everything else (devis, devis acceptés, bons de commande,
            // bons de livraison, bons d'intervention, traites, autre…) has
            // its own dedicated rubric and is NOT a comptable expense.
            const dt = (inv.document_type || "facture").toLowerCase();
            if (dt !== "facture" && dt !== "avoir") continue;
            const name = (inv.vendor || inv.supplier || inv.seller || "Inconnu").trim() || "Inconnu";
            const key = name.toLowerCase();
            const entry = grouped.get(key) || { name, key, count: 0, total: 0, items: [] };
            entry.count += 1;
            let amt = Number(inv.total_ttc) || 0;
            if (dt === "avoir" && amt > 0) amt = -amt;
            entry.total += amt;
            // Collect items for the per-supplier drill-down
            if (Array.isArray(inv.items)) {
                for (const it of inv.items) entry.items.push(it);
            }
            grouped.set(key, entry);
        }
        const all = Array.from(grouped.values()).sort((a, b) => b.count - a.count);
        const totalCount = all.reduce((s, x) => s + x.count, 0) || 1;
        const TOP = 9;
        let arr = all;
        if (all.length > TOP + 1) {
            const top = all.slice(0, TOP);
            const rest = all.slice(TOP);
            arr = [
                ...top,
                {
                    name: `Autres (${rest.length} fournisseurs)`,
                    key: "_other",
                    count: rest.reduce((s, x) => s + x.count, 0),
                    total: rest.reduce((s, x) => s + x.total, 0),
                    items: rest.flatMap((x) => x.items || []),
                    _isOther: true,
                },
            ];
        }
        return arr.map((x, i) => ({
            ...x,
            percent: (x.count / totalCount) * 100,
            color: x._isOther ? "#9CA3AF" : PALETTE[i % PALETTE.length],
        }));
    }, [invoices]);

    // Build top services for the currently-expanded supplier (memoised on
    // its identity so we don't recompute on every render).
    const topServices = useMemo(() => {
        if (!expanded) return [];
        const supp = data.find((d) => d.key === expanded);
        if (!supp || !supp.items?.length) return [];
        // Normalise the title so "Domaine .fr (01/01-31/01)" and
        // "Domaine .fr (01/02-28/02)" collapse to the same service.
        const normalise = (raw) => {
            let s = String(raw || "").trim();
            s = s.replace(/\([^)]*\d{1,2}\/\d{1,2}\/?\d{0,4}[^)]*\)/g, ""); // remove date ranges
            s = s.replace(/\b\d{1,2}\/\d{1,2}\/\d{2,4}\b/g, "");            // bare dates
            s = s.replace(/\b\d+\s*(mois|jours?|an)\b/gi, "");                // "1 mois" / "3 jours"
            s = s.replace(/\s{2,}/g, " ").trim().replace(/[\s,;-]+$/, "");
            return s || raw || "(sans nom)";
        };
        const buckets = new Map();
        for (const it of supp.items) {
            const title = it.title || it.description || it.name || "(sans nom)";
            const key = normalise(title).toLowerCase();
            const entry = buckets.get(key) || { label: normalise(title), count: 0, total: 0 };
            const qty = Number(it.quantity) || 1;
            entry.count += qty;
            entry.total += (Number(it.unit_price) || Number(it.total) || 0) * qty;
            buckets.set(key, entry);
        }
        return Array.from(buckets.values()).sort((a, b) => b.count - a.count).slice(0, 10);
    }, [expanded, data]);

    if (!data.length) {
        return (
            <Card className="ec-card">
                <CardContent className="p-6">
                    <h3 className="font-display text-lg font-bold tracking-tight mb-1">
                        Répartition par fournisseur
                    </h3>
                    <p className="text-xs text-muted-foreground mb-6">
                        Importez vos factures pour voir la répartition.
                    </p>
                    <div className="text-center text-sm text-muted-foreground py-12">
                        Aucune donnée
                    </div>
                </CardContent>
            </Card>
        );
    }

    const dominant = data[0];

    return (
        <Card className="ec-card" data-testid="supplier-pie-card">
            <CardContent className="p-6">
                <div className="flex items-start justify-between mb-1">
                    <h3 className="font-display text-lg font-bold tracking-tight">
                        Répartition par fournisseur
                    </h3>
                    <span className="text-xs text-muted-foreground">
                        {data.length} fournisseur{data.length > 1 ? "s" : ""}
                    </span>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                    Pourcentage de factures par enseigne — utile pour identifier où vous commandez le plus.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
                    {/* Pie */}
                    <div className="md:col-span-2 h-56" data-testid="supplier-pie-chart">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={data}
                                    dataKey="count"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={45}
                                    outerRadius={90}
                                    paddingAngle={0}
                                    stroke="white"
                                    strokeWidth={2}
                                >
                                    {data.map((entry) => (
                                        <Cell key={entry.name} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Legend / ranking */}
                    <div className="md:col-span-3 space-y-1.5 max-h-72 overflow-y-auto pr-2">
                        {data.map((d, i) => {
                            const isOpen = expanded === d.key;
                            return (
                                <div key={d.name}>
                                    <button
                                        type="button"
                                        onClick={() => setExpanded(isOpen ? null : d.key)}
                                        className={`w-full flex items-center justify-between gap-3 px-2 py-1.5 rounded-md transition-colors text-left
                                            ${isOpen ? "bg-accent" : "hover:bg-accent/50"}`}
                                        data-testid={`supplier-row-${i}`}
                                    >
                                        <div className="flex items-center gap-2 min-w-0">
                                            {isOpen ? (
                                                <CaretDown size={12} className="text-muted-foreground shrink-0" />
                                            ) : (
                                                <CaretRight size={12} className="text-muted-foreground shrink-0" />
                                            )}
                                            <span
                                                className="h-3 w-3 rounded-sm shrink-0"
                                                style={{ background: d.color }}
                                                aria-hidden
                                            />
                                            <span className="text-sm font-medium truncate">{d.name}</span>
                                        </div>
                                        <div className="flex items-center gap-3 text-right whitespace-nowrap">
                                            <span className="text-xs text-muted-foreground">{d.count} fact.</span>
                                            <span className="text-sm tabular font-semibold tabular-nums">
                                                {fmtEUR(d.total)}
                                            </span>
                                            <span className="text-xs text-muted-foreground w-12 text-right tabular-nums">
                                                {d.percent.toFixed(1)}%
                                            </span>
                                        </div>
                                    </button>
                                    {isOpen && (
                                        <div className="ml-6 pl-3 border-l-2 mt-1 mb-2 py-2 space-y-1"
                                             style={{ borderColor: d.color }}
                                             data-testid={`supplier-services-${i}`}>
                                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                                                Top services / lignes facturées
                                            </div>
                                            {topServices.length === 0 ? (
                                                <div className="text-xs text-muted-foreground py-1">
                                                    Aucun détail de service dans les factures de ce fournisseur.
                                                </div>
                                            ) : (
                                                topServices.map((s, j) => (
                                                    <div key={s.label + j}
                                                         className="flex items-center justify-between gap-2 text-xs py-1">
                                                        <span className="truncate flex-1" title={s.label}>
                                                            {s.label}
                                                        </span>
                                                        <span className="text-muted-foreground tabular shrink-0">
                                                            ×{s.count}
                                                        </span>
                                                        <span className="font-semibold tabular shrink-0 w-20 text-right">
                                                            {fmtEUR(s.total)}
                                                        </span>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {dominant && (
                    <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
                        <span className="font-semibold text-foreground">{dominant.name}</span> représente{" "}
                        <span className="font-semibold text-foreground">{dominant.percent.toFixed(1)}%</span>{" "}
                        de vos factures ({dominant.count} sur {data.reduce((s, x) => s + x.count, 0)}).
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
